import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  Link,
  Grid,
  Divider,
  Alert,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  WorkOutline as WorkIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as ActivityIcon,
} from '@mui/icons-material';
import { ProspectInterface } from 'api/canprospect/prospect';

const STATUS_COLORS: Record<string, string> = {
  'New Prospect':      '#d7f7fc',
  'Contacted':         '#d7fce4',
  'Replied':           '#d7fce4',
  'Warm Lead':         '#f3fcd7',
  'Qualified Lead':    '#fcf6d7',
  'Proposal Stage':    '#fce6d7',
  'Negotiation Stage': '#fcd7d7',
  'Closed Won':        '#fcd7f1',
  'Closed Lost':       '#e6d7fc',
};

interface GeneralInfoTabProps {
  prospect: ProspectInterface | null;
  loading: boolean;
  error: string | null;
}

function InfoRow({ icon, label, value, href }: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  href?: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      <Box sx={{ color: 'text.secondary', mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}
        >
          {label}
        </Typography>
        {href ? (
          <Link
            href={href}
            underline="hover"
            sx={{ display: 'block', fontWeight: 500, fontSize: '0.875rem' }}
          >
            {value}
          </Link>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: 'grey.50',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 2, py: 1 }}>
        {children}
      </Box>
    </Paper>
  );
}

function LoadingSkeleton() {
  return (
    <Box sx={{ p: 2.5 }}>
      <Grid container spacing={2.5}>
        {[0, 1, 2].map((i) => (
          <Grid item xs={12} md={4} key={i}>
            <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="70%" height={20} />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

const GeneralInfoTab: React.FC<GeneralInfoTabProps> = ({ prospect, loading, error }) => {
  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!prospect) {
    return null;
  }

  const statusColor = STATUS_COLORS[prospect.ProspectStatus?.trim() || ''] || '#e0e0e0';

  return (
    <Box sx={{ p: 2.5 }}>
      <Grid container spacing={2.5}>
        {/* Prospect Details */}
        <Grid item xs={12} md={4}>
          <SectionCard title="Prospect Details">
            <InfoRow
              icon={
                <Chip
                  size="small"
                  label={prospect.ProspectStatus?.trim() || 'N/A'}
                  sx={{
                    bgcolor: statusColor,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 22,
                  }}
                />
              }
              label="Current Status"
              value={prospect.ProspectStatus?.trim() || 'N/A'}
            />
            <Divider />
            <InfoRow
              icon={<BusinessIcon fontSize="small" />}
              label="Standard Industry Classification"
              value={prospect.SicCodeDescription?.trim() || 'N/A'}
            />
          </SectionCard>
        </Grid>

        {/* Contacts */}
        <Grid item xs={12} md={4}>
          <SectionCard title="Contacts">
            <InfoRow
              icon={<BusinessIcon fontSize="small" />}
              label="Customer"
              value={(prospect as any).CustomerName || 'N/A'}
              href={(prospect as any).CustomerId ? `/cancustomer?id=${(prospect as any).CustomerId}` : undefined}
            />
            <Divider />
            <InfoRow
              icon={<PersonIcon fontSize="small" />}
              label="Consultant"
              value={prospect.ConsultantName || 'No assigned consultant'}
              href={prospect.ConsultantId ? `/cancontact?id=${prospect.ConsultantId}` : undefined}
            />
            <Divider />
            <InfoRow
              icon={<PersonIcon fontSize="small" />}
              label="Primary Contact"
              value={(prospect as any).PrimaryContactName || 'No assigned primary contact'}
              href={(prospect as any).PrimaryContactId ? `/cancontact?id=${(prospect as any).PrimaryContactId}` : undefined}
            />
            {prospect.Phone && (
              <>
                <Divider />
                <InfoRow
                  icon={<PhoneIcon fontSize="small" />}
                  label="Phone"
                  value={prospect.Phone}
                  href={`tel:${prospect.Phone}`}
                />
              </>
            )}
            {prospect.Email && (
              <>
                <Divider />
                <InfoRow
                  icon={<EmailIcon fontSize="small" />}
                  label="Email"
                  value={prospect.Email}
                  href={`mailto:${prospect.Email}`}
                />
              </>
            )}
          </SectionCard>
        </Grid>

        {/* Activity */}
        <Grid item xs={12} md={4}>
          <SectionCard title="Activity">
            <InfoRow
              icon={<ActivityIcon fontSize="small" />}
              label="Status"
              value={(prospect as any).StatusTitle || prospect.ProspectStatus || 'N/A'}
            />
            <Divider />
            <InfoRow
              icon={<TimeIcon fontSize="small" />}
              label="Time Spent"
              value={(prospect as any).TimeSpent ?? 'N/A'}
            />
            <Divider />
            <InfoRow
              icon={<WorkIcon fontSize="small" />}
              label="# of Activities"
              value={(prospect as any).NumberOfActivities ?? 'N/A'}
            />
            <Divider />
            <InfoRow
              icon={<CalendarIcon fontSize="small" />}
              label="Created Date"
              value={(prospect as any).CreatedOn || 'N/A'}
            />
            <Divider />
            <InfoRow
              icon={<CalendarIcon fontSize="small" />}
              label="Last Update"
              value={(prospect as any).UpdatedOn || 'N/A'}
            />
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GeneralInfoTab;
